import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface AddressItem {
  address: string;
  label?: string;
  is_headquarters?: boolean;
}

interface AddressManagerProps {
  addresses: AddressItem[];
  onChange: (addresses: AddressItem[]) => void;
  maxAddresses?: number;
}

export function AddressManager({
  addresses,
  onChange,
  maxAddresses = 5,
}: AddressManagerProps) {
  const addAddress = () => {
    if (addresses.length < maxAddresses) {
      onChange([
        ...addresses,
        { address: "", label: "", is_headquarters: addresses.length === 0 },
      ]);
    }
  };

  const updateAddress = (index: number, field: keyof AddressItem, value: string | boolean) => {
    const updated = [...addresses];
    updated[index] = { ...updated[index], [field]: value };
    
    // If setting this as headquarters, unset others
    if (field === "is_headquarters" && value === true) {
      updated.forEach((addr, i) => {
        if (i !== index) {
          addr.is_headquarters = false;
        }
      });
    }
    
    onChange(updated);
  };

  const removeAddress = (index: number) => {
    const updated = addresses.filter((_, i) => i !== index);
    // If we removed the headquarters, set the first one as headquarters
    if (addresses[index]?.is_headquarters && updated.length > 0) {
      updated[0].is_headquarters = true;
    }
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {addresses.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">
          Nenhum endereço cadastrado
        </p>
      ) : (
        addresses.map((addr, index) => (
          <div
            key={index}
            className={cn(
              "agent-card agent-field-enter relative",
              addr.is_headquarters && "ring-2 ring-agent-accent/50"
            )}
          >
            {addr.is_headquarters && (
              <Badge className="absolute -top-2 -right-2 bg-agent-accent text-agent-accent-foreground">
                <Building2 className="w-3 h-3 mr-1" />
                Matriz
              </Badge>
            )}
            
            <div className="flex items-center justify-between gap-2">
              <Input
                value={addr.label || ""}
                onChange={(e) => updateAddress(index, "label", e.target.value)}
                placeholder="Rótulo (ex: Loja Centro, Filial Norte)"
                className="flex-1 text-sm"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeAddress(index)}
                className="text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-md"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <Textarea
              value={addr.address}
              onChange={(e) => updateAddress(index, "address", e.target.value)}
              placeholder="Endereço completo: Rua, Número, Bairro, Cidade/UF, CEP"
              className="min-h-[70px] resize-none"
            />
            
            {!addr.is_headquarters && addresses.length > 1 && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={addr.is_headquarters || false}
                  onCheckedChange={(checked) => updateAddress(index, "is_headquarters", checked)}
                />
                <span className="text-sm text-muted-foreground">
                  Definir como matriz
                </span>
              </div>
            )}
          </div>
        ))
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addAddress}
        disabled={addresses.length >= maxAddresses}
        className="w-full border-dashed"
      >
        <Plus className="w-4 h-4 mr-2" /> Adicionar Endereço
      </Button>

      {addresses.length >= maxAddresses && (
        <p className="text-xs text-muted-foreground text-center">
          Limite de {maxAddresses} endereços atingido
        </p>
      )}
    </div>
  );
}
